<template>
  <div class="workflow-builder">
    <!-- Header -->
    <header class="header">
      <div class="header-content">
        <h1>Workflow Builder</h1>
        <div class="header-actions">
          <div class="dropdown" ref="loadDropdown">
            <button class="btn btn-load" @click="toggleLoadDropdown">
              <span class="btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5"></path></svg>
              </span>
              Load
              <span class="chevron-down">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              </span>
            </button>
            <div class="dropdown-menu" v-if="showLoadDropdown">
              <div v-if="savedWorkflows.length === 0" class="dropdown-empty">
                <span class="empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="15"></line><line x1="15" y1="9" x2="9" y2="15"></line></svg>
                </span>
                <span>No saved workflows</span>
              </div>
              <button 
                v-for="workflow in savedWorkflows" 
                :key="workflow.id" 
                class="dropdown-item"
                @click="loadWorkflow(workflow.id)"
              >
                <div class="item-content">
                  <span class="item-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  </span>
                  <span class="item-name">{{ workflow.name }}</span>
                </div>
                <span class="date">{{ formatDate(workflow.date) }}</span>
              </button>
            </div>
          </div>
          <button 
            class="btn btn-save" 
            :class="{ 'btn-save-active': isDirty }" 
            :disabled="!isDirty"
            @click="saveWorkflow"
          >
            <span class="btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            </span>
            Save
          </button>
        </div>
      </div>
    </header>
  
    <div class="main-content">
      <!-- Left Sidebar -->
      <div class="left-sidebar">
        <div class="sidebar-tabs">
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'triggers' }"
            @click="activeTab = 'triggers'"
          >
            <span class="tab-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            </span>
            Triggers
          </button>
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'actions' }"
            @click="activeTab = 'actions'"
          >
            <span class="tab-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            </span>
            Actions
          </button>
        </div>
        
        <div class="sidebar-content">
          <!-- Triggers Tab -->
          <div v-if="activeTab === 'triggers'" class="tab-content">
            <div 
              v-for="trigger in triggers" 
              :key="trigger.type"
              class="sidebar-item"
              :class="`sidebar-item-${trigger.type}`"
              draggable="true"
              @dragstart="onDragStart($event, trigger)"
            >
              <div class="sidebar-item-icon" v-html="trigger.icon"></div>
              <div class="sidebar-item-label">{{ trigger.label }}</div>
            </div>
          </div>
          
          <!-- Actions Tab -->
          <div v-if="activeTab === 'actions'" class="tab-content">
            <div 
              v-for="action in actions" 
              :key="action.type"
              class="sidebar-item"
              :class="`sidebar-item-${action.type}`"
              draggable="true"
              @dragstart="onDragStart($event, action)"
            >
              <div class="sidebar-item-icon" v-html="action.icon"></div>
              <div class="sidebar-item-label">{{ action.label }}</div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Canvas -->
      <div 
        class="canvas" 
        ref="canvas"
        @mousedown="startDrag($event, null)"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @dragover.prevent
        @drop="onDrop"
      >
        <!-- Grid Background -->
        <svg class="grid-background" width="100%" height="100%">
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f0f0f0" stroke-width="1"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
  
        <!-- Connections -->
        <svg class="connections" width="100%" height="100%">
          <g v-for="connection in connections" :key="connection.id">
            <path 
              :d="getConnectionPath(connection)" 
              stroke="#a5b4fc" 
              stroke-width="2" 
              stroke-dasharray="5,5" 
              fill="none" 
              class="connection-path"
            />
            <circle 
              :cx="getConnectionPointCoordinates(nodes.find(n => n.id === connection.source), 'bottom').x" 
              :cy="getConnectionPointCoordinates(nodes.find(n => n.id === connection.source), 'bottom').y" 
              r="4" 
              fill="#6366f1" 
              class="connection-point-circle"
            />
            <circle 
              :cx="getConnectionPointCoordinates(nodes.find(n => n.id === connection.target), 'top').x" 
              :cy="getConnectionPointCoordinates(nodes.find(n => n.id === connection.target), 'top').y" 
              r="4" 
              fill="#6366f1" 
              class="connection-point-circle"
            />
          </g>
        </svg>
  
        <!-- Connection in progress -->
        <svg v-if="connectionInProgress" class="temp-connection" width="100%" height="100%">
          <path 
            :d="getTempConnectionPath()" 
            stroke="#6366f1" 
            stroke-width="2.5" 
            stroke-dasharray="5,5" 
            fill="none" 
            class="temp-connection-path"
          />
        </svg>
  
        <!-- Nodes -->
        <div 
          v-for="node in nodes" 
          :key="node.id"
          class="node"
          :class="[`node-${node.type}`, { 'selected': selectedNode === node.id }]"
          :style="{ 
            left: `${node.x}px`, 
            top: `${node.y}px`,
            backgroundColor: node.backgroundColor || getDefaultColor(node.type)
          }"
          @mousedown.stop="startDrag($event, node.id)"
          :data-id="node.id"
        >
          <!-- Node Header -->
          <div class="node-header">
            <div class="node-icon" v-html="getNodeIcon(node.type)">
              
            </div>
            <div class="node-title">{{ getNodeTitle(node) }}</div>
            <button class="node-menu-btn" @click.stop="openNodeSettings(node)">
              <span class="dots">⋮</span>
            </button>
          </div>
  
          <!-- Node Content -->
          <div class="node-content">
            <div v-if="node.type === 'greeting'" class="greeting-content">
              {{ node.content || 'Hello, this is Sam calling from Codebase.' }}
            </div>
            <div v-else-if="node.type === 'question'" class="question-content">
              {{ node.content || 'I\'m calling to discuss our new service that helps businesses like yours. Do you have a moment?' }}
            </div>
            <div v-else-if="node.type === 'response'" class="response-content">
              <div class="response-label">Response Options:</div>
              <div v-if="node.options && node.options.length" class="response-options">
                <div v-for="(option, index) in node.options" :key="index" class="response-option">
                  {{ option }}
                  <button class="remove-option" @click.stop="removeOption(node, index)">×</button>
                </div>
              </div>
              <div class="add-option">
                <span class="plus">+</span> New option...
              </div>
            </div>
            <div v-else-if="node.type === 'assign-task'" class="task-content">
              <div class="task-label">Task: {{ node.taskName || 'Untitled Task' }}</div>
              <div class="task-assignee">Assigned to: {{ node.assignee || 'Unassigned' }}</div>
            </div>
            <div v-else-if="node.type === 'send-email'" class="email-content">
              <div class="email-label">Email: {{ node.emailSubject || 'No Subject' }}</div>
              <div class="email-recipient">To: {{ node.emailRecipient || 'No Recipient' }}</div>
            </div>
            <div v-else-if="node.type === 'send-text'" class="text-content">
              <div class="text-label">SMS Message</div>
              <div class="text-recipient">To: {{ node.textRecipient || 'No Recipient' }}</div>
            </div>
            <div v-else-if="node.type === 'webhook'" class="webhook-content">
              <div class="webhook-label">Webhook</div>
              <div class="webhook-url">URL: {{ node.webhookUrl || 'No URL configured' }}</div>
            </div>
            <div v-else-if="node.type === 'delay'" class="delay-content">
              <div class="delay-label">Delay</div>
              <div class="delay-duration">Duration: {{ node.delayValue || 1 }} {{ node.delayUnit || 'hours' }}</div>
            </div>
            <div v-else-if="node.type === 'send-slack'" class="slack-content">
              <div class="slack-label">Slack Message</div>
              <div class="slack-channel">Channel: {{ node.slackChannelName ? '#' + node.slackChannelName : 'Not selected' }}</div>
              <div v-if="node.slackMessage" class="slack-preview">{{ node.slackMessage.substring(0, 50) }}{{ node.slackMessage.length > 50 ? '...' : '' }}</div>
            </div>
            <div v-else-if="node.type === 'appointment-completed'" class="appointment-content">
              <div class="appointment-label">Appointment</div>
              <div class="appointment-details">
                <div class="appointment-type">Type: {{ node.appointmentType || 'Not specified' }}</div>
                <div v-if="node.sendFollowUp" class="appointment-followup">Follow-up enabled</div>
                <div v-if="node.requireFeedback" class="appointment-feedback">Feedback required</div>
              </div>
            </div>
          </div>
  
          <!-- Connection Points -->
          <div class="connection-point connection-point-top" @mousedown.stop="startConnection(node.id, 'top')"></div>
          <div class="connection-point connection-point-bottom" @mousedown.stop="startConnection(node.id, 'bottom')"></div>
        </div>
      </div>
    </div>
  
    <!-- Node Settings Sidebar -->
    <div class="settings-sidebar" v-if="showSettings" :class="{ 'show': showSettings, 'has-unsaved-changes': hasUnsavedChanges }">
      <div class="settings-header">
        <h2>
          Configure {{ getNodeTitle(selectedNodeData) }}
          <span v-if="hasUnsavedChanges" class="unsaved-indicator">*</span>
        </h2>
        <button class="close-btn" @click="closeSettings">×</button>
      </div>
      <div class="settings-content">
        <p class="settings-description">Adjust properties for this workflow node.</p>
        
        <!-- Node Type Selection (only for trigger nodes) -->
        <div class="setting-section" v-if="isTriggerNode(selectedNodeData.type)">
          <label>Node Type</label>
          <div class="node-type-tabs">
            <button 
              v-for="trigger in triggers"
              :key="trigger.type"
              class="node-type-tab" 
              :class="{ 'active': selectedNodeData.type === trigger.type }"
              @click="updateNodeType(trigger.type)"
            >
              <span class="tab-icon" v-html="trigger.icon"></span>
              {{ trigger.label }}
            </button>
          </div>
        </div>
  
        <!-- Appointment Completed Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'appointment-completed'">
          <label>Title</label>
          <input type="text" v-model="selectedNodeData.title" placeholder="Enter title" class="form-control">
          
          <label class="mt-3">Description</label>
          <textarea v-model="selectedNodeData.description" placeholder="Enter description" rows="3" class="form-control"></textarea>
          
          <label class="mt-3">Appointment Type</label>
          <select v-model="selectedNodeData.appointmentType" class="form-control">
            <option value="">Select type...</option>
            <option value="regular">Regular Checkup</option>
            <option value="emergency">Emergency</option>
            <option value="cleaning">Cleaning</option>
            <option value="consultation">Consultation</option>
          </select>

          <label class="mt-3">Patient Notes</label>
          <textarea v-model="selectedNodeData.patientNotes" placeholder="Enter any patient-specific notes" rows="4" class="form-control"></textarea>
          
          <div class="checkbox-group mt-3">
            <label class="checkbox-container">
              <input type="checkbox" v-model="selectedNodeData.sendFollowUp">
              <span class="checkbox-label">Send follow-up reminder</span>
            </label>
            
            <label class="checkbox-container">
              <input type="checkbox" v-model="selectedNodeData.requireFeedback">
              <span class="checkbox-label">Require patient feedback</span>
            </label>
          </div>

          <div class="file-upload mt-3">
            <label>Attached Documents</label>
            <div class="upload-area" @click="triggerFileUpload">
              <input type="file" ref="fileInput" style="display: none" multiple @change="handleFileUpload">
              <div class="upload-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                <p>Click to upload or drag and drop</p>
                <span>Support for PDF, DOC, DOCX (max 10MB)</span>
              </div>
            </div>
            <div v-if="selectedNodeData.files && selectedNodeData.files.length" class="file-list">
              <div v-for="(file, index) in selectedNodeData.files" :key="index" class="file-item">
                <span class="file-name">{{ file.name }}</span>
                <button class="remove-file" @click="removeFile(index)">×</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Node Type Selection (only for action nodes) -->
        <div class="setting-section" v-if="isActionNode(selectedNodeData.type)">
          <label>Node Type</label>
          <div class="node-type-tabs">
            <button 
              v-for="action in actions"
              :key="action.type"
              class="node-type-tab" 
              :class="{ 'active': selectedNodeData.type === action.type }"
              @click="updateNodeType(action.type)"
            >
              <span class="tab-icon" v-html="action.icon"></span>
              {{ action.label }}
            </button>
          </div>
        </div>
  
        <!-- Task Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'assign-task'">
          <label>Task Name</label>
          <input type="text" v-model="selectedNodeData.taskName" placeholder="Enter task name">
          
          <label class="mt-3">Assignee</label>
          <input type="text" v-model="selectedNodeData.assignee" placeholder="Enter assignee name">
          
          <label class="mt-3">Description</label>
          <textarea v-model="selectedNodeData.taskDescription" placeholder="Enter task description" rows="3"></textarea>
          
          <label class="mt-3">Due Date</label>
          <input type="date" v-model="selectedNodeData.dueDate">
        </div>
  
        <!-- Email Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'send-email'">
          <label>Email Subject</label>
          <input type="text" v-model="selectedNodeData.emailSubject" placeholder="Enter email subject">
          
          <label class="mt-3">Recipient</label>
          <input type="email" v-model="selectedNodeData.emailRecipient" placeholder="Enter recipient email">
          
          <label class="mt-3">Email Body</label>
          <textarea v-model="selectedNodeData.emailBody" placeholder="Enter email content" rows="5"></textarea>
        </div>
  
        <!-- Text Message Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'send-text'">
          <label>Recipient Phone</label>
          <input type="tel" v-model="selectedNodeData.textRecipient" placeholder="Enter phone number">
          
          <label class="mt-3">Message</label>
          <textarea v-model="selectedNodeData.textMessage" placeholder="Enter text message" rows="3"></textarea>
        </div>
  
        <!-- Webhook Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'webhook'">
          <label>Webhook URL</label>
          <input type="url" v-model="selectedNodeData.webhookUrl" placeholder="Enter webhook URL">
          
          <label class="mt-3">Method</label>
          <select v-model="selectedNodeData.webhookMethod">
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
          
          <label class="mt-3">Headers</label>
          <textarea v-model="selectedNodeData.webhookHeaders" placeholder="Enter headers as JSON" rows="3"></textarea>
          
          <label class="mt-3">Body</label>
          <textarea v-model="selectedNodeData.webhookBody" placeholder="Enter body as JSON" rows="3"></textarea>
        </div>
  
        <!-- Delay Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'delay'">
          <label>Delay Duration</label>
          <div class="delay-inputs">
            <input type="number" v-model="selectedNodeData.delayValue" min="1" class="delay-value">
            <select v-model="selectedNodeData.delayUnit" class="delay-unit">
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
        </div>

        <!-- Slack Message Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'send-slack'">
          <div v-if="!slackConnected" class="slack-not-connected">
            <div class="alert alert-warning">
              <strong>Slack Not Connected</strong>
              <p>You need to connect your Slack workspace in Settings before using this action.</p>
              <a href="/app/settings" class="btn btn-sm btn-primary">Go to Settings</a>
            </div>
          </div>
          
          <div v-else>
            <label>Slack Channel</label>
            <select v-model="selectedNodeData.slackChannelId" @change="updateSlackChannelName">
              <option value="">-- Select a channel --</option>
              <option v-for="channel in slackChannels" :key="channel.id" :value="channel.id">
                {{ channel.is_private ? '🔒 ' : '#' }}{{ channel.name }}
              </option>
            </select>
            
            <label class="mt-3">Message</label>
            <textarea 
              v-model="selectedNodeData.slackMessage" 
              :placeholder="'Enter the message to send to Slack. You can use variables like {patient_name}, {appointment_date}, etc.'"
              rows="5"
            ></textarea>
            
            <div class="variable-help mt-2">
              <small class="text-muted">
                <strong>Available variables:</strong> 
                <code v-pre>{{patient_name}}</code>, 
                <code v-pre>{{patient_phone}}</code>, 
                <code v-pre>{{appointment_date}}</code>, 
                <code v-pre>{{appointment_time}}</code>, 
                <code v-pre>{{provider_name}}</code>
              </small>
            </div>
          </div>
        </div>
  
        <!-- Task Settings Table (for greeting and question nodes) -->
        <div class="setting-section" v-if="['greeting', 'question'].includes(selectedNodeData.type)">
          <div class="section-header">
            <h3>Task Settings</h3>
            <button class="add-task-btn" @click="addTask">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              Add Task
            </button>
          </div>
          
          <!-- Empty state when no tasks -->
          <div v-if="(selectedNodeData.tasks || []).length === 0" class="empty-tasks">
            <div class="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
            </div>
            <p>No tasks added yet</p>
          </div>
          
          <!-- Task table -->
          <div v-else class="task-table-container">
            <table class="task-table">
              <thead>
                <tr>
                  <th>Code Reference</th>
                  <th>Assigned To</th>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th>Wiki</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(task, index) in selectedNodeData.tasks" :key="index">
                  <td class="code-cell">
                    <div class="code-content">
                      <input type="text" v-model="task.code" placeholder="Code name">
                      <input type="text" v-model="task.url" placeholder="URL (optional)" class="url-input">
                    </div>
                  </td>
                  <td>
                    <input type="text" v-model="task.assignee" placeholder="Assignee">
                  </td>
                  <td>
                    <textarea v-model="task.description" placeholder="Task description"></textarea>
                  </td>
                  <td>
                    <input type="date" v-model="task.dueDate">
                  </td>
                  <td>
                    <select v-model="task.wiki">
                      <option value="">Select...</option>
                      <option value="documentation">Documentation</option>
                      <option value="guidelines">Guidelines</option>
                    </select>
                  </td>
                  <td>
                    <button class="delete-task-btn" @click="removeTask(index)" title="Delete Task">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
  
        <!-- Background Color -->
        <div class="setting-section">
          <label>Background Color</label>
          <div class="color-options">
            <div 
              v-for="color in colorOptions" 
              :key="color.value"
              class="color-option"
              :style="{ backgroundColor: color.value }"
              :class="{ 'selected': selectedNodeData.backgroundColor === color.value }"
              @click="updateBackgroundColor(color.value)"
            ></div>
          </div>
        </div>
  
        <!-- Node Content -->
        <div class="setting-section" v-if="['greeting', 'question'].includes(selectedNodeData.type)">
          <label>Content</label>
          <textarea 
            v-model="selectedNodeData.content" 
            :placeholder="getContentPlaceholder(selectedNodeData.type)"
            rows="4"
          ></textarea>
        </div>
  
        <!-- Response Options -->
        <div class="setting-section" v-if="selectedNodeData.type === 'response'">
          <label>Response Options</label>
          <div class="response-options-editor">
            <div v-for="(option, index) in selectedNodeData.options || []" :key="index" class="response-option-item">
              <input type="text" v-model="selectedNodeData.options[index]" placeholder="Option text">
              <button class="remove-option-btn" @click="removeOption(selectedNodeData, index)">×</button>
            </div>
            <button class="add-option-btn" @click="addOption">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              Add Option
            </button>
          </div>
        </div>
  
        <!-- Unsaved Changes Notification -->
        <div v-if="hasUnsavedChanges" class="unsaved-changes-notification">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
          You have unsaved changes
        </div>
  
        <!-- Action Buttons -->
        <div class="settings-actions">
          <button class="delete-node-btn" @click="deleteSelectedNode">
            <span class="btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </span> 
            Delete Node
          </button>
          <div class="right-actions">
            <button class="cancel-btn" @click="closeSettings">Cancel</button>
            <button class="save-btn" :class="{ 'save-btn-highlight': hasUnsavedChanges }" @click="saveSettings">
              <span class="btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
              </span>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Save Workflow Modal -->
    <div class="modal" v-if="showSaveModal">
      <div class="modal-overlay" @click="showSaveModal = false"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>Save Workflow</h3>
          <button class="close-btn" @click="showSaveModal = false">×</button>
        </div>
        <div class="modal-body">
          <label for="workflow-name">Workflow Name</label>
          <input 
            type="text" 
            id="workflow-name" 
            v-model="workflowName" 
            placeholder="Enter a name for this workflow"
            @keyup.enter="confirmSave"
          >
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showSaveModal = false">Cancel</button>
          <button class="save-btn" @click="confirmSave">
            <span class="btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            </span>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
  </template>
  
  <script>
  import { mapState, mapActions } from 'vuex';
  
  export default {
  name: 'WorkflowBuilder',
  data() {
    return {
      activeTab: 'triggers',
      nodes: [],
      connections: [],
      nextNodeId: 1,
      nextConnectionId: 1,
      selectedNode: null,
      draggingNode: null,
      dragOffset: { x: 0, y: 0 },
      showSettings: false,
      selectedNodeData: {},
      tempNodeData: {},
      connectionInProgress: null,
      mousePosition: { x: 0, y: 0 },
      isDirty: false,
      hasUnsavedChanges: false,
      showSaveModal: false,
      workflowName: '',
      savedWorkflows: [],
      showLoadDropdown: false,
      colorOptions: [
        { name: 'Light Blue', value: '#e3f2fd' },
        { name: 'Light Green', value: '#e8f5e9' },
        { name: 'Light Yellow', value: '#fffde7' },
        { name: 'Light Purple', value: '#f3e5f5' },
        { name: 'Light Pink', value: '#fce4ec' },
        { name: 'White', value: '#ffffff' }
      ],
      triggers: [
        { type: 'appointment-completed', label: 'Appointment Completed', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><path d="M9 14l2 2 4-4"></path></svg>' },
        { type: 'greeting', label: 'Greeting', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>' },
        { type: 'question', label: 'Question', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>' },
        { type: 'response', label: 'Response Branch', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' },
        { type: 'mangovoice', label: 'Mango Voice', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>' }
      ],
      actions: [
        { type: 'assign-task', label: 'Assign Task', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M9 14l2 2 4-4"></path></svg>' },
        { type: 'send-email', label: 'Send Email', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>' },
        { type: 'send-text', label: 'Send Text', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>' },
        { type: 'send-slack', label: 'Send Slack Message', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"></path><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"></path><path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"></path><path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"></path><path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z"></path><path d="M15.5 19H14v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"></path><path d="M10 9.5C10 8.67 9.33 8 8.5 8h-5C2.67 8 2 8.67 2 9.5S2.67 11 3.5 11h5c.83 0 1.5-.67 1.5-1.5z"></path><path d="M8.5 5H10V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5z"></path></svg>' },
        { type: 'webhook', label: 'Webhook', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>' },
        { type: 'delay', label: 'Delay', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>' }
      ],
      // Slack integration data
      slackConnected: false,
      slackChannels: [],
      slackLoading: false
    };
  },
  mounted() {
    // Load saved workflows from localStorage
    this.loadSavedWorkflowsList();
    
    // Load Slack integration data
    this.loadSlackData();
    
    // Add click outside listener for dropdown
    document.addEventListener('click', this.handleClickOutside);
    
    // Set window title with asterisk when there are unsaved changes
    this.$watch('hasUnsavedChanges', (newVal) => {
      if (newVal) {
        document.title = '* Workflow Builder';
      } else {
        document.title = 'Workflow Builder';
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.title = 'Workflow Builder'; // Reset title
  },
  computed: {
    ...mapState({
      siteID: state => state.customer.siteID
    })
  },
  methods: {
    ...mapActions('slack', {
      slackGetStatus: 'getSlackStatus',
      slackGetChannels: 'getChannels'
    }),
    
    // Slack integration methods
    async loadSlackData() {
      if (!this.siteID) return;
      
      try {
        this.slackLoading = true;
        const statusResponse = await this.slackGetStatus({ siteID: this.siteID });
        this.slackConnected = statusResponse.connected;
        
        if (this.slackConnected) {
          const channelsResponse = await this.slackGetChannels({ siteID: this.siteID });
          this.slackChannels = channelsResponse.channels || [];
        }
      } catch (err) {
        console.error('Error loading Slack data:', err);
        this.slackConnected = false;
        this.slackChannels = [];
      } finally {
        this.slackLoading = false;
      }
    },
    
    updateSlackChannelName() {
      if (this.selectedNodeData.slackChannelId) {
        const channel = this.slackChannels.find(c => c.id === this.selectedNodeData.slackChannelId);
        if (channel) {
          this.selectedNodeData.slackChannelName = channel.name;
        }
      }
    },
    
    // Save/Load functionality
    loadSavedWorkflowsList() {
      try {
        const savedWorkflowsJson = localStorage.getItem('workflowBuilderList');
        if (savedWorkflowsJson) {
          this.savedWorkflows = JSON.parse(savedWorkflowsJson);
        }
      } catch (error) {
        console.error('Error loading saved workflows list:', error);
        this.savedWorkflows = [];
      }
    },
    saveWorkflow() {
      this.workflowName = '';
      this.showSaveModal = true;
    },
    confirmSave() {
      if (!this.workflowName.trim()) {
        this.workflowName = `Workflow ${new Date().toLocaleString()}`;
      }
      
      // Deep clone the data to remove Vue reactivity and avoid circular references
      const workflowData = {
        nodes: JSON.parse(JSON.stringify(this.nodes)),
        connections: JSON.parse(JSON.stringify(this.connections)),
        nextNodeId: this.nextNodeId,
        nextConnectionId: this.nextConnectionId
      };
      
      const workflowId = `workflow_${Date.now()}`;
      const workflowMeta = {
        id: workflowId,
        name: this.workflowName,
        date: new Date().toISOString()
      };

      console.log('workflowData', workflowData);
      
      // Save workflow data
      try {
        localStorage.setItem(`workflow_${workflowId}`, JSON.stringify(workflowData));
        
        // Update workflow list
        this.savedWorkflows.push(workflowMeta);
        localStorage.setItem('workflowBuilderList', JSON.stringify(this.savedWorkflows));
        
        this.isDirty = false;
        this.showSaveModal = false;
      } catch (error) {
        console.error('Error saving workflow:', error);
        alert('Failed to save workflow. Local storage may be full or unavailable.');
      }
    },
    loadWorkflow(workflowId) {
      try {
        const workflowJson = localStorage.getItem(`workflow_${workflowId}`);
        if (workflowJson) {
          const workflowData = JSON.parse(workflowJson);
          
          // Load the workflow data
          this.nodes = workflowData.nodes;
          this.connections = workflowData.connections;
          this.nextNodeId = workflowData.nextNodeId;
          this.nextConnectionId = workflowData.nextConnectionId;
          
          this.isDirty = false;
          this.showLoadDropdown = false;
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        alert('Failed to load workflow. The saved data may be corrupted.');
      }
    },
    toggleLoadDropdown() {
      this.showLoadDropdown = !this.showLoadDropdown;
    },
    handleClickOutside(event) {
      if (this.$refs.loadDropdown && !this.$refs.loadDropdown.contains(event.target)) {
        this.showLoadDropdown = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    
    // Drag and drop functionality
    onDragStart(event, item) {
      event.dataTransfer.setData('nodeType', item.type);
      // Use a transparent 1px image instead of the ghost element
      const emptyImage = new Image();
      emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      event.dataTransfer.setDragImage(emptyImage, 0, 0);
    },
    onDrop(event) {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('nodeType');
      if (nodeType) {
        const canvasRect = this.$refs.canvas.getBoundingClientRect();
        const x = event.clientX - canvasRect.left - 120; // Half node width
        const y = event.clientY - canvasRect.top - 60;  // Half node height
        
        this.addNode(nodeType, x, y);
        this.setDirty();
      }
    },
    addNode(type, x = 250, y = 100) {
      const id = this.nextNodeId++;
      const node = {
        id,
        type,
        x: x,
        y: y + (this.nodes.length % 3 * 20), // Slight offset for multiple nodes
        backgroundColor: this.getDefaultColor(type),
        content: '',
        tasks: []
      };
      
      // Add type-specific properties
      if (type === 'response') {
        node.options = ['Yes', 'No'];
      } else if (type === 'assign-task') {
        node.taskName = 'New Task';
        node.assignee = '';
        node.taskDescription = '';
        node.dueDate = '';
      } else if (type === 'send-email') {
        node.emailSubject = '';
        node.emailRecipient = '';
        node.emailBody = '';
      } else if (type === 'send-text') {
        node.textRecipient = '';
        node.textMessage = '';
      } else if (type === 'webhook') {
        node.webhookUrl = '';
        node.webhookMethod = 'POST';
        node.webhookHeaders = '';
        node.webhookBody = '';
      } else if (type === 'delay') {
        node.delayValue = 1;
        node.delayUnit = 'hours';
      } else if (type === 'send-slack') {
        node.slackChannelId = '';
        node.slackChannelName = '';
        node.slackMessage = '';
      }
      
      this.nodes.push(node);
      this.setDirty();
      return id;
    },
    getDefaultColor(type) {
      switch(type) {
        case 'greeting': return '#e3f2fd'; // Light blue
        case 'question': return '#e8f5e9'; // Light green
        case 'response': return '#fffde7'; // Light yellow
        case 'assign-task': return '#f3e5f5'; // Light purple
        case 'send-email': return '#e1f5fe'; // Light blue
        case 'send-text': return '#e0f7fa'; // Light cyan
        case 'webhook': return '#f1f8e9'; // Light green
        case 'delay': return '#fff3e0'; // Light orange
        case 'send-slack': return '#f3e5f5'; // Light purple (Slack brand-ish)
        default: return '#ffffff';
      }
    },
    getNodeIcon(type) {
      const item = [...this.triggers, ...this.actions].find(item => item.type === type);
      return item ? item.icon : '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>';
    },
    getNodeTitle(node) {
      if (!node) return '';
      
      const item = [...this.triggers, ...this.actions].find(item => item.type === node.type);
      return item ? item.label : 'Node';
    },
    isTriggerNode(type) {
      return this.triggers.some(trigger => trigger.type === type);
    },
    isActionNode(type) {
      return this.actions.some(action => action.type === type);
    },
    getContentPlaceholder(type) {
      switch(type) {
        case 'greeting': return 'Enter greeting message...';
        case 'question': return 'Enter question...';
        default: return 'Enter content...';
      }
    },
    startDrag(event, nodeId) {
      if (nodeId) {
        const node = this.nodes.find(n => n.id === nodeId);
        if (node) {
          this.draggingNode = nodeId;
          this.dragOffset = {
            x: event.clientX - node.x,
            y: event.clientY - node.y
          };
          this.selectedNode = nodeId;
        }
      }
    },
    onDrag(event) {
      // Update mouse position for connection drawing
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      this.mousePosition = {
        x: event.clientX - canvasRect.left,
        y: event.clientY - canvasRect.top
      };
      
      // Handle node dragging
      if (this.draggingNode) {
        const node = this.nodes.find(n => n.id === this.draggingNode);
        if (node) {
          node.x = event.clientX - this.dragOffset.x;
          node.y = event.clientY - this.dragOffset.y;
          this.setDirty();
        }
      }
    },
    stopDrag() {
      // If we're creating a connection
      if (this.connectionInProgress && this.connectionInProgress.target) {
        const sourceNode = this.nodes.find(n => n.id === this.connectionInProgress.source);
        const targetNode = this.nodes.find(n => n.id === this.connectionInProgress.target);
        
        if (sourceNode && targetNode && sourceNode.id !== targetNode.id) {
          this.createConnection(sourceNode.id, targetNode.id);
          this.setDirty();
        }
      }
      
      this.draggingNode = null;
      this.connectionInProgress = null;
    },
    startConnection(nodeId, point) {
      this.connectionInProgress = {
        source: nodeId,
        sourcePoint: point,
        target: null
      };
      
      // Add mouse move and up handlers for the entire document
      document.addEventListener('mousemove', this.updateTempConnection);
      document.addEventListener('mouseup', this.finishConnection);
    },
    updateTempConnection(event) {
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      this.mousePosition = {
        x: event.clientX - canvasRect.left,
        y: event.clientY - canvasRect.top
      };
      
      // Check if mouse is over a connection point
      const nodeElements = document.querySelectorAll('.node');
      let foundTarget = false;
      
      nodeElements.forEach(el => {
        const nodeId = parseInt(el.getAttribute('data-id'));
        if (nodeId !== this.connectionInProgress.source) {
          const points = el.querySelectorAll('.connection-point');
          points.forEach(point => {
            const rect = point.getBoundingClientRect();
            const isOver = (
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom
            );
            
            if (isOver) {
              this.connectionInProgress.target = nodeId;
              this.connectionInProgress.targetPoint = point.classList.contains('connection-point-top') ? 'top' : 'bottom';
              foundTarget = true;
            }
          });
        }
      });
      
      if (!foundTarget) {
        this.connectionInProgress.target = null;
        this.connectionInProgress.targetPoint = null;
      }
    },
    finishConnection() {
      document.removeEventListener('mousemove', this.updateTempConnection);
      document.removeEventListener('mouseup', this.finishConnection);
      
      if (this.connectionInProgress && this.connectionInProgress.target) {
        this.createConnection(
          this.connectionInProgress.source,
          this.connectionInProgress.target,
          this.connectionInProgress.sourcePoint,
          this.connectionInProgress.targetPoint
        );
      }
      
      this.connectionInProgress = null;
    },
    createConnection(sourceId, targetId, sourcePoint = 'bottom', targetPoint = 'top') {
      // Check if connection already exists
      const exists = this.connections.some(c => 
        c.source === sourceId && c.target === targetId
      );
      
      if (!exists) {
        this.connections.push({
          id: this.nextConnectionId++,
          source: sourceId,
          target: targetId,
          sourcePoint,
          targetPoint
        });
        this.setDirty();
      }
    },
    getConnectionPath(connection) {
      const sourceNode = this.nodes.find(n => n.id === connection.source);
      const targetNode = this.nodes.find(n => n.id === connection.target);
      
      if (!sourceNode || !targetNode) return '';
      
      // Calculate connection points
      const sourcePoint = this.getConnectionPointCoordinates(sourceNode, 'bottom');
      const targetPoint = this.getConnectionPointCoordinates(targetNode, 'top');
      
      // Calculate distance between points
      const dx = targetPoint.x - sourcePoint.x;
      const dy = targetPoint.y - sourcePoint.y;
      
      // Calculate control points for a smooth S-curve
      const curveStrength = Math.min(Math.abs(dy) * 0.5, 80); // Limit the curve strength
      
      // Create a smooth bezier curve path with S-curve for horizontal offset
      return `M ${sourcePoint.x} ${sourcePoint.y} 
          C ${sourcePoint.x} ${sourcePoint.y + curveStrength} 
            ${targetPoint.x} ${targetPoint.y - curveStrength} 
            ${targetPoint.x} ${targetPoint.y}`;
    },
    getConnectionPointCoordinates(node, point) {
      const nodeWidth = 240; // Approximate node width
      const nodeHeight = 120; // Approximate node height
      
      if (point === 'top') {
        return { x: node.x + nodeWidth / 2, y: node.y };
      } else if (point === 'bottom') {
        return { x: node.x + nodeWidth / 2, y: node.y + nodeHeight };
      }
      
      return { x: node.x, y: node.y };
    },
    getTempConnectionPath() {
      if (!this.connectionInProgress) return '';
      
      const sourceNode = this.nodes.find(n => n.id === this.connectionInProgress.source);
      if (!sourceNode) return '';
      
      const sourcePoint = this.getConnectionPointCoordinates(sourceNode, this.connectionInProgress.sourcePoint);
      
      let targetPoint;
      if (this.connectionInProgress.target) {
        const targetNode = this.nodes.find(n => n.id === this.connectionInProgress.target);
        if (targetNode) {
          targetPoint = this.getConnectionPointCoordinates(targetNode, this.connectionInProgress.targetPoint || 'top');
        } else {
          targetPoint = this.mousePosition;
        }
      } else {
        targetPoint = this.mousePosition;
      }
      
      // Calculate distance between points
      const dx = targetPoint.x - sourcePoint.x;
      const dy = targetPoint.y - sourcePoint.y;
      
      // Calculate control points for a smooth S-curve
      const curveStrength = Math.min(Math.abs(dy) * 0.5, 80); // Limit the curve strength
      
      // Create a smooth bezier curve path with S-curve for horizontal offset
      return `M ${sourcePoint.x} ${sourcePoint.y} 
          C ${sourcePoint.x} ${sourcePoint.y + curveStrength} 
            ${targetPoint.x} ${targetPoint.y - curveStrength} 
            ${targetPoint.x} ${targetPoint.y}`;
    },
    openNodeSettings(node) {
      this.selectedNode = node.id;
      this.selectedNodeData = JSON.parse(JSON.stringify(node)); // Deep copy
      this.tempNodeData = JSON.parse(JSON.stringify(node)); // For cancel operation
      this.showSettings = true;
      this.hasUnsavedChanges = false; // Reset unsaved changes flag
    },
    closeSettings() {
      if (this.hasUnsavedChanges) {
        if (confirm('You have unsaved changes. Are you sure you want to close?')) {
          this.showSettings = false;
          this.selectedNodeData = {};
          this.hasUnsavedChanges = false;
        }
      } else {
        this.showSettings = false;
        this.selectedNodeData = {};
      }
    },
    saveSettings() {
      const index = this.nodes.findIndex(n => n.id === this.selectedNode);
      if (index !== -1) {
        // Update the node with the edited data
        this.nodes.splice(index, 1, this.selectedNodeData);
        this.setDirty();
        this.hasUnsavedChanges = false;
      }
      this.showSettings = false;
    },
    deleteSelectedNode() {
      if (this.selectedNode) {
        // Remove connections involving this node
        this.connections = this.connections.filter(c => 
          c.source !== this.selectedNode && c.target !== this.selectedNode
        );
        
        // Remove the node
        this.nodes = this.nodes.filter(n => n.id !== this.selectedNode);
        
        this.closeSettings();
        this.setDirty();
      }
    },
    updateNodeType(type) {
      this.selectedNodeData.type = type;
      this.selectedNodeData.backgroundColor = this.getDefaultColor(type);
      
      // Reset options for response type
      if (type === 'response' && (!this.selectedNodeData.options || this.selectedNodeData.options.length === 0)) {
        this.selectedNodeData.options = ['Yes', 'No'];
      }
      
      this.hasUnsavedChanges = true;
    },
    updateBackgroundColor(color) {
      this.selectedNodeData.backgroundColor = color;
      this.hasUnsavedChanges = true;
    },
    addTask() {
      if (!this.selectedNodeData.tasks) {
        this.selectedNodeData.tasks = [];
      }
      
      this.selectedNodeData.tasks.push({
        code: '',
        url: '',
        assignee: '',
        description: '',
        dueDate: null,
        wiki: null
      });
      
      this.hasUnsavedChanges = true;
    },
    removeTask(index) {
      this.selectedNodeData.tasks.splice(index, 1);
      this.hasUnsavedChanges = true;
    },
    addOption() {
      if (!this.selectedNodeData.options) {
        this.selectedNodeData.options = [];
      }
      
      this.selectedNodeData.options.push('New option');
      this.hasUnsavedChanges = true;
    },
    removeOption(node, index) {
      if (node.options) {
        node.options.splice(index, 1);
        this.hasUnsavedChanges = true;
      }
    },
    // Track changes to enable/disable save button
    setDirty() {
      this.isDirty = true;
    },

    // File handling methods
    triggerFileUpload() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileUpload(event) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      const files = Array.from(event.target.files);
      const validFiles = files.filter(file => {
        const isValidType = allowedTypes.includes(file.type);
        const isValidSize = file.size <= maxSize;
        
        if (!isValidType) {
          console.warn(`Invalid file type: ${file.type}`);
        }
        if (!isValidSize) {
          console.warn(`File too large: ${file.name}`);
        }
        
        return isValidType && isValidSize;
      });

      if (!this.selectedNodeData.files) {
        this.selectedNodeData.files = [];
      }

      validFiles.forEach(file => {
        this.selectedNodeData.files.push({
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        });
      });

      if (validFiles.length > 0) {
        this.hasUnsavedChanges = true;
      }

      // Reset the input
      event.target.value = '';
    },

    removeFile(index) {
      if (this.selectedNodeData.files) {
        this.selectedNodeData.files.splice(index, 1);
        this.hasUnsavedChanges = true;
      }
    },

    // Appointment trigger helpers
    getAppointmentTypeLabel(type) {
      const types = {
        regular: 'Regular Checkup',
        emergency: 'Emergency',
        cleaning: 'Cleaning',
        consultation: 'Consultation'
      };
      return types[type] || 'Not specified';
    }
  }
  };
  </script>
  
  <style scoped>
  /* Global Styles */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  }
  
  body {
    background-color: #f8f9fa;
    color: #333;
    line-height: 1.5;
  }
  
  /* Workflow Builder Container */
  .workflow-builder {
    display: flex;
    flex-direction: column;
    height: 100vh;
    position: relative;
    overflow: hidden;
    background-color: #f9fafb;
  }
  
  /* Header */
  .header {
    background: linear-gradient(to right, #6366f1, #8b5cf6);
    color: white;
    padding: 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }
  
  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .header h1 {
    font-size: 20px;
    font-weight: 600;
    color: white;
    margin: 0;
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    height: 40px;
    min-width: 100px;
  }
  
  .btn-save {
    background-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
    cursor: not-allowed;
  }
  
  .btn-save-active {
    background-color: #22c55e;
    color: white;
    cursor: pointer;
  }
  
  .btn-save-active:hover {
    background-color: #16a34a;
    transform: translateY(-1px);
  }
  
  .btn-load {
    background-color: white;
    color: #6366f1;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .btn-load:hover {
    background-color: #f9fafb;
    transform: translateY(-1px);
  }
  
  .chevron-down {
    font-size: 10px;
    margin-left: 4px;
  }
  
  /* Dropdown */
  .dropdown {
    position: relative;
  }
  
  .dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 280px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 100;
    overflow: hidden;
    max-height: 350px;
    overflow-y: auto;
    animation: fadeIn 0.2s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .dropdown-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    text-align: left;
    background: none;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
  }
  
  .dropdown-item:hover {
    background-color: #f5f7fa;
  }
  
  .dropdown-item:last-child {
    border-bottom: none;
  }
  
  .item-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .item-icon {
    font-size: 16px;
  }
  
  .item-name {
    font-weight: 500;
  }
  
  .dropdown-empty {
    padding: 20px;
    text-align: center;
    color: #94a3b8;
    font-style: italic;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  
  .empty-icon {
    font-size: 24px;
    margin-bottom: 4px;
  }
  
  .date {
    font-size: 12px;
    color: #94a3b8;
  }
  
  /* Modal */
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
  
  .modal-content {
    position: relative;
    width: 450px;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    animation: modalFadeIn 0.3s ease;
    z-index: 1001;
    overflow: hidden;
  }
  
  @keyframes modalFadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f9fafb;
  }
  
  .modal-header h3 {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  .modal-body {
    padding: 24px;
  }
  
  .modal-body label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
  }
  
  .modal-body input {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  .modal-body input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
  }
  
  /* Main Content */
  .main-content {
    display: flex;
    flex: 1;
    overflow: hidden;
  }
  
  /* Left Sidebar */
  .left-sidebar {
    width: 280px;
    background-color: white;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    z-index: 5;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.03);
  }
  
  .sidebar-tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f9fafb;
  }
  
  .tab-button {
    flex: 1;
    padding: 14px 12px;
    text-align: center;
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  
  .tab-button.active {
    color: #6366f1;
    border-bottom: 2px solid #6366f1;
    background-color: white;
  }
  
  .tab-button:hover:not(.active) {
    background-color: #f3f4f6;
  }
  
  .sidebar-content {
    flex: 1;
    overflow-y: auto;
  }
  
  .tab-content {
    padding: 16px;
  }
  
  .sidebar-item {
    display: flex;
    align-items: center;
    padding: 12px;
    margin-bottom: 10px;
    border-radius: 8px;
    background-color: #f9fafb;
    cursor: grab;
    transition: all 0.2s;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  
  .sidebar-item:hover {
    background-color: #f3f4f6;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    border-color: #d1d5db;
  }
  
  .sidebar-item-icon {
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background-color: white;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    color: #6366f1;
  }
  
  .sidebar-item-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }
  
  /* Canvas */
  .canvas {
    flex: 1;
    position: relative;
    overflow: auto;
    background-color: #f9fafb;
  }
  
  .grid-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }
  
  /* Connections */
  .connections {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }
  
  .connection-path {
    stroke: #a5b4fc;
    stroke-width: 2;
    stroke-dasharray: 5,5;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
    transition: stroke 0.3s, stroke-width 0.3s, filter 0.3s;
    animation: flow 1s linear infinite;
  }
  
  .connection-point-circle {
    fill: #6366f1;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
    transition: fill 0.3s, r 0.3s, filter 0.3s;
  }
  
  .connections g:hover .connection-path {
    stroke: #818cf8;
    stroke-width: 3;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  }
  
  .connections g:hover .connection-point-circle {
    fill: #4f46e5;
    r: 5;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  }
  
  .temp-connection-path {
    animation: flow 1s linear infinite;
  }
  
  @keyframes flow {
    to {
      stroke-dashoffset: -10;
    }
  }
  
  @keyframes dash {
    to {
      stroke-dashoffset: 20;
    }
  }
  
  /* Nodes */
  .node {
    position: absolute;
    width: 240px;
    min-height: 120px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    z-index: 3;
    cursor: move;
    user-select: none;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.3s, transform 0.3s;
  }
  
  .node:hover {
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.07);
    transform: translateY(-3px);
  }
  
  .node.selected {
    box-shadow: 0 0 0 2px #6366f1, 0 12px 20px rgba(0, 0, 0, 0.07);
  }
  
  .node-greeting {
    background-color: #eff6ff;
    border-color: #dbeafe;
  }
  
  .node-question {
    background-color: #f0fdf4;
    border-color: #dcfce7;
  }
  
  .node-response {
    background-color: #fffde7;
    border-color: #fef08a;
  }
  
  .node-assign-task {
    background-color: #f5f3ff;
    border-color: #ede9fe;
  }
  
  .node-send-email {
    background-color: #ecfeff;
    border-color: #cffafe;
  }
  
  .node-send-text {
    background-color: #e0f2fe;
    border-color: #bae6fd;
  }
  
  .node-webhook {
    background-color: #eef2ff;
    border-color: #e0e7ff;
  }
  
  .node-delay {
    background-color: #fff7ed;
    border-color: #ffedd5;
  }

  .node-send-slack {
    background-color: #f5f3ff;
    border-color: #e9d5ff;
  }
  
  .node-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    background-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(4px);
  }
  
  .node-icon {
    margin-right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: #f9fafb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    color: #6366f1;
  }
  
  .node-title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  
  .node-menu-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 4px;
    color: #6b7280;
    transition: background-color 0.2s, color 0.2s;
  }
  
  .node-menu-btn:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #374151;
  }
  
  .dots {
    display: inline-block;
    transform: rotate(90deg);
  }
  
  .node-content {
    padding: 16px;
    flex: 1;
    font-size: 14px;
    color: #4b5563;
  }
  
  .greeting-content, .question-content {
    color: #4b5563;
    line-height: 1.5;
  }
  
  .response-content, .task-content, .email-content, .text-content, .webhook-content, .delay-content, .slack-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .response-label, .task-label, .email-label, .text-label, .webhook-label, .delay-label, .slack-label {
    font-weight: 600;
    margin-bottom: 4px;
    color: #374151;
  }

  .slack-channel {
    font-size: 13px;
    color: #6b7280;
  }

  .slack-preview {
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
  }
  
  .response-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .response-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }
  
  .remove-option {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: #9ca3af;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s, color 0.2s;
  }
  
  .remove-option:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
  
  .add-option {
    display: flex;
    align-items: center;
    color: #4a6cf7;
    cursor: pointer;
    padding: 6px 0;
    font-size: 13px;
    font-weight: 500;
    transition: color 0.2s;
  }
  
  .add-option:hover {
    color: #3451b2;
  }
  
  .plus {
    margin-right: 4px;
    font-weight: bold;
  }
  
  /* Connection Points */
  .connection-point {
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: white;
    border: 2px solid #6366f1;
    border-radius: 50%;
    z-index: 4;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  }
  
  .connection-point:hover {
    background-color: #6366f1;
    border-color: #4f46e5;
    transform: scale(1.3);
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.2);
  }
  
  .connection-point-top {
    top: -7px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .connection-point-bottom {
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  /* Settings Sidebar */
  .settings-sidebar {
    position: fixed;
    top: 0;
    right: -800px;
    width: 800px;
    height: 100vh;
    background-color: white;
    box-shadow: -5px 0 25px rgba(0, 0, 0, 0.1);
    z-index: 100;
    transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 1px solid #e5e7eb;
  }
  
  .settings-sidebar.show {
    right: 0;
  }
  
  .settings-sidebar.has-unsaved-changes {
    border-left: 2px solid #f59e0b;
  }
  
  .settings-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f9fafb;
  }
  
  .settings-header h2 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
  }
  
  .unsaved-indicator {
    color: #f59e0b;
    margin-left: 6px;
    font-size: 20px;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: background-color 0.2s, color 0.2s;
  }
  
  .close-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
  
  .settings-content {
    padding: 20px;
    overflow-y: auto;
    height: calc(100vh - 70px);
  }
  
  .settings-description {
    color: #6b7280;
    margin-bottom: 24px;
    font-size: 14px;
    line-height: 1.5;
  }
  
  .setting-section {
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .setting-section:last-child {
    border-bottom: none;
  }
  
  .setting-section label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
  }
  
  .setting-section input[type="text"],
  .setting-section input[type="email"],
  .setting-section input[type="tel"],
  .setting-section input[type="url"],
  .setting-section input[type="date"],
  .setting-section input[type="number"],
  .setting-section textarea,
  .setting-section select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #374151;
    background-color: white;
    transition: border-color 0.2s, box-shadow 0.2s;
    margin-bottom: 12px;
  }
  
  .setting-section input:focus,
  .setting-section textarea:focus,
  .setting-section select:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  .mt-3 {
    margin-top: 16px;
  }
  
  .delay-inputs {
    display: flex;
    gap: 10px;
  }
  
  .delay-value {
    flex: 1;
  }
  
  .delay-unit {
    width: 120px;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  
  .section-header h3 {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  .add-task-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: #4a6cf7;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: color 0.2s;
  }
  
  .add-task-btn:hover {
    color: #3451b2;
  }
  
  /* Task Table */
  .task-table-container {
    overflow-x: auto;
    margin-top: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    /* Remove max-width to allow table to use full width of the wider sidebar */
  }
  
  .task-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: fixed;
  }
  
  .task-table th {
    background-color: #f9fafb;
    padding: 12px;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }
  
  .task-table td {
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
  }
  
  .task-table tr:last-child td {
    border-bottom: none;
  }
  
  .task-table input,
  .task-table textarea,
  .task-table select {
    width: 100%;
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
  }
  
  .task-table textarea {
    min-height: 80px;
    resize: vertical;
  }
  
  .code-cell {
    width: 160px;
  }
  
  .task-table th:nth-child(1) { width: 160px; }
  .task-table th:nth-child(2) { width: 120px; }
  .task-table th:nth-child(3) { width: 230px; }
  .task-table th:nth-child(4) { width: 100px; }
  .task-table th:nth-child(5) { width: 100px; }
  .task-table th:nth-child(6) { width: 40px; }
  
  .delete-task-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #ef4444;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
  }
  
  .delete-task-btn:hover {
    background-color: #fee2e2;
  }
  
  .empty-tasks {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    background-color: #f9fafb;
    border-radius: 8px;
    border: 1px dashed #d1d5db;
    color: #6b7280;
    text-align: center;
  }
  
  .empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background-color: #f3f4f6;
    border-radius: 50%;
    margin-bottom: 12px;
    color: #9ca3af;
  }
  
  /* Node Type Tabs */
  .node-type-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
  }
  
  .node-type-tab {
    padding: 10px 14px;
    text-align: center;
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
    font-weight: 500;
    color: #4b5563;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .node-type-tab:hover {
    background-color: #f3f4f6;
    border-color: #d1d5db;
  }
  
  .node-type-tab.active {
    background-color: #6366f1;
    color: white;
    border-color: #6366f1;
  }
  
  /* Color Options */
  .color-options {
    display: flex;
    gap: 12px;
    margin-top: 12px;
    flex-wrap: wrap;
  }
  
  .color-option {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .color-option:hover {
    transform: scale(1.1);
  }
  
  .color-option.selected {
    box-shadow: 0 0 0 2px white, 0 0 0 4px #4a6cf7;
  }
  
  /* Unsaved Changes Notification */
  .unsaved-changes-notification {
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: #fffbeb;
    border-top: 1px solid #fef3c7;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #d97706;
    font-weight: 500;
    margin: 0 -20px;
    width: calc(100% + 40px);
    z-index: 10;
  }
  
  /* Settings Actions */
  .settings-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 32px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
  }
  
  .right-actions {
    display: flex;
    gap: 16px;
  }
  
  .delete-node-btn {
    padding: 12px 18px;
    background-color: #ef4444;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background-color 0.2s;
  }
  
  .delete-node-btn:hover {
    background-color: #dc2626;
  }
  
  .cancel-btn {
    padding: 12px 18px;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    color: #4b5563;
    font-weight: 500;
    transition: background-color 0.2s;
  }
  
  .cancel-btn:hover {
    background-color: #e5e7eb;
  }
  
  .save-btn {
    padding: 12px 18px;
    background-color: #4a6cf7;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
  }
  
  .save-btn:hover {
    background-color: #3451b2;
  }
  
  .save-btn-highlight {
    background-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
    50% { transform: scale(1.02); box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
  }
  
  /* Form Controls */
  .form-control {
    width: 100%;
    padding: 10px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #374151;
    background-color: white;
    transition: all 0.2s ease;
    min-height: 42px;
  }

  /* Custom select styling */
  select.form-control {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding-right: 40px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  select.form-control option {
    padding: 12px;
    font-size: 14px;
    color: #374151;
    background-color: white;
  }

  .form-control:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  /* Checkbox Group */
  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 24px 0;
    padding: 16px;
    background-color: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .checkbox-container {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .checkbox-container input[type="checkbox"] {
    margin-top: 2px;
  }

  .checkbox-label {
    padding-top: 0;
    line-height: 24px;
  }

  .checkbox-container:hover {
    background-color: #f3f4f6;
  }

  .checkbox-container input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
  }

  .checkbox-container input[type="checkbox"]:checked {
    background-color: #6366f1;
    border-color: #6366f1;
  }

  .checkbox-container input[type="checkbox"]:checked::after {
    content: '';
    position: absolute;
    left: 6px;
    top: 2px;
    width: 4px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .checkbox-container input[type="checkbox"]:hover {
    border-color: #6366f1;
  }

  .checkbox-container input[type="checkbox"]:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }

  .checkbox-label {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
    user-select: none;
  }

  /* File Upload */
  .file-upload {
    margin-top: 24px;
  }

  .upload-area {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .upload-area:hover {
    border-color: #6366f1;
    background-color: #f8fafc;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #6b7280;
  }

  .upload-placeholder svg {
    color: #6366f1;
    margin-bottom: 4px;
  }

  .upload-placeholder p {
    margin: 0;
    font-weight: 500;
    color: #374151;
  }

  .upload-placeholder span {
    font-size: 12px;
    color: #9ca3af;
  }

  .file-list {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background-color: #f3f4f6;
    border-radius: 6px;
    font-size: 13px;
  }

  .file-name {
    color: #374151;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .file-name::before {
    content: '';
    width: 4px;
    height: 4px;
    background-color: #6366f1;
    border-radius: 50%;
    display: inline-block;
  }

  .remove-file {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .remove-file:hover {
    background-color: #fee2e2;
  }

  /* Response Options Editor */
  .response-options-editor {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .response-option-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .remove-option-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #ef4444;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
  }
  
  .remove-option-btn:hover {
    background-color: #fee2e2;
  }
  
  .add-option-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: #4a6cf7;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    margin-top: 6px;
    transition: color 0.2s;
  }
  
  .add-option-btn:hover {
    color: #3451b2;
  }
  
  /* Responsive Styles */
  @media (max-width: 1024px) {
    .header-content {
      padding: 10px 16px;
    }
  
    .header h1 {
      font-size: 18px;
    }
  
    .btn {
      padding: 8px 12px;
      font-size: 13px;
    }
  
    .left-sidebar {
      width: 250px;
    }
  
    .settings-sidebar {
      width: 700px;
    }
  }
  
  @media (max-width: 768px) {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  
    .header-actions {
      width: 100%;
      justify-content: space-between;
    }
  
    .settings-sidebar {
      width: 100%;
      right: -100%;
    }
  
    .node {
      width: 220px;
    }
  
    .left-sidebar {
      width: 220px;
    }
  
    .sidebar-item-icon {
      margin-right: 8px;
      font-size: 18px;
      width: 30px;
      height: 30px;
    }
  
    .sidebar-item-label {
      font-size: 13px;
    }
  
    .modal-content {
      width: 90%;
      max-width: 400px;
    }
  }
  
  @media (max-width: 640px) {
    .left-sidebar {
      width: 180px;
    }
  
    .tab-button {
      padding: 10px 8px;
      font-size: 13px;
    }
  
    .sidebar-item {
      padding: 10px 8px;
    }
  
    .node {
      width: 200px;
    }
  
    .node-header {
      padding: 10px 12px;
    }
  
    .node-content {
      padding: 12px;
      font-size: 13px;
    }
  }
  
  /* Animations */
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
    50% { transform: scale(1.02); box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
  }
  
  .btn-save-active {
    animation: pulse 2s infinite;
  }
  
  .btn-save-active:hover {
    animation: none;
  }
  </style>
